import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Heart, Trophy, Anchor, Sparkles, UserRound, Globe, CheckCircle, Shield, Map, Camera, Star, Sun, Instagram } from 'lucide-react';
import { pageHeroImages, experiencesData as fallbackExperiencesData } from '../mock';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Plane,
  Heart,
  Trophy,
  Anchor,
  Sparkles,
  UserRound,
  Globe,
  CheckCircle,
  Shield,
  Map,
  Camera,
  Star,
  Sun
};

const Experiences = () => {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const response = await axios.get(`${API}/experiences`);
        if (response.data && response.data.length > 0) {
          setExperiences(response.data);
        } else {
          setExperiences(fallbackExperiencesData);
        }
      } catch (error) {
        console.error('Error fetching experiences:', error);
        setExperiences(fallbackExperiencesData);
      } finally {
        setLoading(false);
      }
    };

    fetchExperiences();
  }, []);

  const handleCtaClick = () => {
    navigate('/design-journey');
  };

  if (loading) {
    return (
      <div className="page-container">
        <section className="page-hero" style={{ backgroundImage: `url(${pageHeroImages.experiences})` }}>
          <div className="page-hero-overlay"></div>
          <div className="page-hero-content">
            <h1 className="page-title">Our Experiences</h1>
            <p className="page-subtitle">Curated journeys designed exclusively for you</p>
          </div>
        </section>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading experiences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="experiences-page">
      <section className="page-hero" style={{ backgroundImage: `url(${pageHeroImages.experiences})` }}>
        <div className="page-hero-overlay"></div>
        <div className="page-hero-content">
          <h1 className="page-title">Our Experiences</h1>
          <p className="page-subtitle">Curated journeys designed exclusively for you</p>
        </div>
      </section>

      <section className="experiences-detail-section">
        <div className="section-container">
          {experiences.map((experience, index) => {
            const IconComponent = iconMap[experience.icon];
            return (
              <div 
                key={experience.id} 
                className={`experience-detail-card ${index % 2 === 0 ? 'experience-detail-normal' : 'experience-detail-reverse'}`}
                data-testid={`experience-detail-${experience.id}`}
              >
                <div className="experience-detail-image-container">
                  <img 
                    src={experience.image} 
                    alt={experience.title || experience.category} 
                    className="experience-detail-image" 
                  />
                  {IconComponent && (
                    <div className="experience-detail-icon">
                      <IconComponent size={32} />
                    </div>
                  )}
                </div>
                <div className="experience-detail-content">
                  <h2 className="experience-detail-title">{experience.title || experience.category}</h2>
                  <p className="experience-detail-description">{experience.description}</p>
                  {experience.features && experience.features.length > 0 && (
                    <ul className="experience-detail-features">
                      {experience.features.map((feature, idx) => (
                        <li key={idx} className="experience-detail-feature">{feature}</li>
                      ))}
                    </ul>
                  )}
                  <div className="experience-detail-actions">
                    <Button 
                      onClick={handleCtaClick} 
                      className="experience-cta-btn"
                      data-testid={`experience-cta-${experience.id}`}
                    >
                      Enquire Now
                    </Button>
                    {experience.instagram_link && (
                      <a 
                        href={experience.instagram_link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="experience-instagram-btn"
                        data-testid={`experience-instagram-${experience.id}`}
                        title="View on Instagram"
                      >
                        <Instagram size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="experiences-cta-section">
        <div className="section-container">
          <h2 className="experiences-cta-title">Ready to Begin Your Journey?</h2>
          <p className="experiences-cta-text">Let us craft an experience tailored exclusively to you</p>
          <Button 
            onClick={handleCtaClick} 
            className="experiences-main-cta"
            data-testid="experiences-main-cta"
          >
            Design My Journey
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Experiences;
